<template>
	<div class="container py-5">
        <h2>{{ $t('activityPlan') }}</h2>
        <nav aria-label="breadcrumb" class="mb-4">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink :to="`/modelings`">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="`/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}`">
                        {{ $t('modelingResult') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('activityPlan') }}</li>
            </ol>
        </nav>
		<div v-if="pending">
            <div class="d-flex justify-content-center loading-center">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="row mb-4">
                <div class="col-md-4">
                    <label class="form-label" for="limits" required>{{ $t('limits') }}</label>
                    <select id="limits" class="form-select" v-model.number="data.limit">
                        <option value="">{{ $t('select') }}</option>
                        <template v-if="limitList.length > 0" >
                            <option v-for="(limit, i) in limitList" :key="i" v-bind:value="limit.id">{{ limit.name }}</option>
                        </template>
                    </select>
                </div>
                <div class="col-md-4">
                    <label class="form-label" for="startYear" required>{{ $t('startYear') }}</label>
                    <input v-model.number="data.selectionStartYear" :min="data.selectionStartYear" type="number" class="form-control" id="startYear" >
                </div>
                <div class="col-md-4">
                    <label class="form-label" for="endYear" required>{{ $t('endYear') }}</label>
                    <input v-model.number="data.selectionEndYear" :max="data.selectionEndYear" type="number" class="form-control" id="endYear" >
                </div>
            </div>
            <div class="mb-4">
                <h4 class="mb-2">{{ $t('assetType') }}</h4>
                <div v-for="(assetType, i) in assetTypeList" v-bind:key="i"  class="mb-3 border-bottom">
                    <div class="row">
                        <div class="col-md-2">
                            <div class="input-group mb-3">
                                <div class="input-group-text">
                                    <input class="form-check-input mt-0" type="checkbox" v-model="assetType.checked" :id="'forecast_' + i">
                                </div>
                                <label type="text" :for="'forecast_' + i" class="form-control" ><b>{{assetType.name}}</b></label>
                            </div>
                        </div>
                        <div v-for="(activity, i) in assetType.activities" v-bind:key="i"  class="mb-3 col-md-3">
                            <div class="input-group mb-3">
                                <div class="input-group-text">
                                    <input class="form-check-input mt-0" type="checkbox" :disabled="!assetType.checked" v-model="activity.checked" :id="'activity_' + activity.id">
                                </div>
                                <label type="text" :for="'activity_' + activity.id" class="form-control">{{activity.name}}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="button-group">
                <button :disabled="!data.limit" @click="exportActivityPlan()" class="btn btn-sm btn-primary mb-4" >Export csv</button>
                <button :disabled="!data.limit" @click="exportShp()" class="btn btn-sm btn-primary mb-4 ms-2" >Export shp</button>
            </div>
            <!-- content.center -->
            <div v-if="renderMap && mapCenter" class="mb-3 overflow-auto">  
                <nav>
                    <div class="nav nav-tabs" id="nav-tab" role="tablist">
                        <button class="nav-link active" id="map-tab" data-bs-toggle="tab" data-bs-target="#map" type="button" role="tab" aria-controls="map" aria-selected="true">{{ $t('map')}}</button>
                        <button class="nav-link" @click="adjustDt()" id="data-tab" data-bs-toggle="tab" data-bs-target="#data" type="button" role="tab" aria-controls="data" aria-selected="false">{{$t('data')}}</button>
                    </div>
                </nav>
                <div class="tab-content" id="nav-tabContent">
                    <div class="tab-pane fade show active" id="map" role="tabpanel" aria-labelledby="map-tab">
                        <div v-if="mapLoading" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); padding: 10px;">
                            <span class="spinner-border" role="status" aria-hidden="true"></span>
                        </div>
                        <div ref="map" class="map"></div>
                        <div style="display: none">
                            <div id="popup" class="ol-popup" ref="popup">
                                <div class="btn-group btn-group-toggle" data-toggle="buttons" style="width:100%">
                                </div>
                                <a href="#" id="popup-closer" class="ol-popup-closer" ref="popupCloser"></a>
                                <div id="popup-content">
                                    <table class="display compact responsive nowrap" width="100%" ref="popUpDataTable">
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="tab-pane fade" id="data" role="tabpanel" aria-labelledby="data-tab">
                        <DataTable
                            ref="dt"
                            :pageLength="5"
                            :lengthMenu="[ 5, 25, 50, 75, 100 ]"
                            :columns="tableColumns"
                            :options="tableOptions"
                            :data="tableData"
                            class="display"
                        />
                    </div>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex mb-4">
                <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}` })">{{ $t('back') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, nextTick } from 'vue';
import AnalysisApi from "../../api/server/analysis";
import ModelingApi from "../../api/server/modeling";
import { CheckIfUserIsEditor } from "../../authCache";
import DataTable from 'datatables.net-vue3';
import DataTablesLib from 'datatables.net';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Map from 'ol/Map';
import View from 'ol/View';
import Overlay from 'ol/Overlay';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { getFeaturesFromSections, getFeaturesFromSectionGeoJSON, defaultLineStyleFunction } from "../../utils/feature-ext";
import { Select } from 'ol/interaction';
import { parseCoords } from "../../utils/geom-coords";
import 'ol/ol.css';
import 'ol-layerswitcher/dist/ol-layerswitcher.css';
import debounce from 'lodash.debounce';
import Control from 'ol/control/Control';

DataTable.use(DataTablesLib);

export default {
    name: "resultCharts",
    components: {
        DataTable
    },
    watch: {
        $route: "requestData",
        data: {
              handler() {
                this.debouncedUpdateFeatures();
            },
            deep: true
        },
        assetTypeList: {
            handler() {
                this.debouncedUpdateFeatures();
            },
            deep: true
        }
    },
    mounted() {
        this.requestData();
    },
    created() {
        this.debouncedUpdateFeatures = debounce(this.updateFeatures.bind(this), 200);
    },
    computed: {
        filteredSections() {
            const from = this.data.selectionStartYear;
            const to = this.data.selectionEndYear;

            // vybrané assetTypes
            const selectedAssetTypeIds = this.assetTypeList.filter(at => at.checked).map(at => at.id);

            // vybrané activity
            const selectedActivityIds = this.assetTypeList.flatMap(at => {
                if (!at.checked) return [];
                const subs = at.activities.filter(a => a.checked).map(a => a.id);
                // Když nejsou vybrané žádné aktivity, vrátí se všechny aktivity daného assetType
                return subs.length ? subs : at.activities.map(a => a.id);
            });

            return this.activityPlanProperties.filter(sec => {
                if (this.data.limit != null && sec.limitId != this.data.limit)
                    return false;
                if (selectedAssetTypeIds.length && !selectedAssetTypeIds.includes(sec.assetTypeId))
                    return false;

                if (from && to) {
                    const inYears = sec.activities.some(a => {
                        const y = a.year;
                        return y >= from && y <= to;
                    });

                    if (!inYears) 
                        return false;
                }

                if (selectedActivityIds.length) {
                    const anySelectedActivities = sec.activities.some(a => selectedActivityIds.includes(a.id));
                    if (!anySelectedActivities)
                        return false;
                }
                
                return true;
            }).map(sec => ({
                ...sec,
                activities: (sec.activities || []).filter(a => {
                    const passActivity = !selectedActivityIds.length || selectedActivityIds.includes(a.id);
                    const passYear = !(from && to) || (a.year >= from && a.year <= to);

                    return passActivity && passYear;
                })
            }));
        }
    },
    methods: {
        async requestData() {
            this.isUserAdmin = await CheckIfUserIsEditor();

            var analysis = await AnalysisApi.analysisDetail(this.$route.params.id, this.$route.params.modelingResultId);
            var selection = analysis.modelingInfo.selections.find(s => s.id == analysis.selectionId);

            this.modelingName = analysis.modelingInfo.name;
            this.data.selectionStartYear = analysis.selectionStartYear;
            this.data.selectionEndYear = analysis.selectionEndYear;

            var sections = await AnalysisApi.activityPlanSections(this.$route.params.id, this.$route.params.modelingResultId);
            for (var i in sections.data) {
                var dataset = sections.data[i];
                this.recById[i] = Object.fromEntries(dataset.map(r => [String(r.properties.OBJECTID), r]));
            }

            var activityPlan = await AnalysisApi.activityPlanData(this.$route.params.id, this.$route.params.modelingResultId, "");

            // ZLO/!!!!
            // var usedAssetTypes = {};
            // for (var i in activityPlan.properties) {
            //     usedAssetTypes[activityPlan.properties[i].assetTypeId] = true;
            // }

            const usedActivityIds = new Set(activityPlan.usedActivityIds);
            const usedAssetTypeIds = new Set(activityPlan.usedAssetTypeIds);

            this.assetTypeList = analysis.modelingInfo.assetTypes.filter(a => usedAssetTypeIds.has(a.id)).map(assetType => ({
                ...assetType,
                activities: assetType.activities.filter(a => usedActivityIds.has(a.id))
            }));

            this.mapCenter = activityPlan?.center;
            this.activityPlanProperties = activityPlan.properties;
            this.limitList = selection.limits;
            this.pending = false;
            
            if(this.renderMap && this.mapCenter) {
                await nextTick();
                this.adjustDt();
                this.initMap();
            }
        },
        async initMap() {
            var self = this;
            self.mapLoading = true;

            if (!self.$refs.map) {
                return;
            }

            // Release previous map
            if (self.map) {
                self.map.setTarget(undefined);
            }

            if (self.popUpDataTable) {
                self.popUpDataTable.destroy(true);
            }

            const overlay = new Overlay({
                element: self.$refs.popup,
                autoPan: {
                    animation: {
                        duration: 250,
                    },
                },
            });

            if (self.$refs["popupCloser"]) {
                self.$refs["popupCloser"].onclick = () => {
                    overlay.setPosition(undefined);
                    self.$refs["popupCloser"].blur();
                    return false;
                };
            }

            this.vectorLayer = new VectorLayer({
                source: new VectorSource(),
                style: (feature, res) => defaultLineStyleFunction(feature, res, feature.get("color") || "#000000", 4),
            });

            const layers = [
                new TileLayer({ source: new OSM() }),
                this.vectorLayer
            ];

          
            // const features = getFeaturesFromSectionGeoJSON(self.filteredSections, projection);
            // if (features.length > 0) {
            //     const vector = new VectorLayer({ 
            //         source: new VectorSource({ features: features }),
            //         style: (feature, resolution) => defaultLineStyleFunction(feature, resolution, feature.get("color") ? feature.get("color") : "#000000", 4)
            //     });
            //     layers.push(vector);
            // }
            // this.content.center misto pevneho center
            const view = new View({ center: parseCoords(this.mapCenter), zoom: 12});
            self.map = new Map({
                target: self.$refs.map,
                layers: layers,
                overlays: [overlay],
                view: view,
            });

            const select = new Select({
                style: function (feature, resolution) {
                    return defaultLineStyleFunction(feature, resolution, "#A020F0", 4);

                },
            });

            select.on('select', (evt) => {
                if (evt.selected && evt.selected.length > 0) {
                    self.selectedFeatures = evt.target.getFeatures().getArray();
                    self.selectedFeatureIndex = self.selectedFeatures.length;

                    self.setPopupTableData();
                    overlay.setPosition(evt.mapBrowserEvent.coordinate);
                } else {
                    self.selectedFeatures = [];
                    self.selectedFeatureIndex = 0;
                    overlay.setPosition(undefined);
                    self.$refs.popupCloser.blur();
                }
            });

            self.map.addInteraction(select);

            this.updateFeatures();
            this.buildLegendItems();
            this.renderLegendControl();

            self.mapLoading = false;
        },
        buildLegendItems() {
            var activityColors = [];

            this.filteredSections.forEach(sec => {
                (sec.activities || []).forEach(act => {
                    const label = act.name ?? '—';
                    const color = act.color || '#000000';

                    if(activityColors.some(ac => ac.label === label && ac.color === color)) 
                        return;
                    activityColors.push({ label, color });
                });
            });

            this.legendItems = activityColors;
        },
        renderLegendControl() {
            if (this.legendItems.length == 0) return;
           
            const el = document.createElement('div');
            el.className = 'ol-unselectable ol-control activity-legend';
            el.style = 'top:.5em; right:.5em;'; // pravý horní roh

            el.innerHTML = `
                <div class="legend-card">
                <div class="legend-title">${this.$t('legend')}</div>
                <ul class="legend-list">
                    ${this.legendItems.map(it => `
                    <li class="legend-row">
                        <span class="legend-dot" style="background:${it.color}"></span>
                        <span class="legend-label" title="${it.label}">${it.label}</span>
                    </li>
                    `).join('')}
                </ul>
                </div>
            `;

            this.legendControl = new Control({ element: el });
            this.map.addControl(this.legendControl);
        },  
        exportActivityPlan() {
            var query = "?";
            if (this.data.limit) {
                query += `limitId=${this.data.limit}&`;
            }
            if (this.data.selectionStartYear) {
                query += `startYear=${this.data.selectionStartYear}&`;
            }
            if (this.data.selectionEndYear) {
                query += `endYear=${this.data.selectionEndYear}&`;
            }

            const assetTypeIds = this.assetTypeList.filter(at => at.checked).map(at => at.id);
            if (assetTypeIds.length) {
                query += `assetTypeIds=${assetTypeIds.join(',')}&`;
            }

            const activityIds = this.assetTypeList.flatMap(at => {
                if (!at.checked) return [];
                const sel = at.activities.filter(a => a.checked).map(a => a.id);
                return sel.length ? sel : at.activities.map(a => a.id);
            });

            if (activityIds.length) {
                query += `activityIds=${activityIds.join(',')}&`;
            }
 
            var request = AnalysisApi.exportActivityPlanCsv(this.$route.params.id, this.$route.params.modelingResultId, query);
            window.open(request.url);
        },
        exportShp() {
               var query = "?";
            if (this.data.limit) {
                query += `limitId=${this.data.limit}&`;
            }
            if (this.data.selectionStartYear) {
                query += `startYear=${this.data.selectionStartYear}&`;
            }
            if (this.data.selectionEndYear) {
                query += `endYear=${this.data.selectionEndYear}&`;
            }

            const assetTypeIds = this.assetTypeList.filter(at => at.checked).map(at => at.id);
            if (assetTypeIds.length) {
                query += `assetTypeIds=${assetTypeIds.join(',')}&`;
            }

            const activityIds = this.assetTypeList.flatMap(at => {
                if (!at.checked) return [];
                const sel = at.activities.filter(a => a.checked).map(a => a.id);
                return sel.length ? sel : at.activities.map(a => a.id);
            });

            if (activityIds.length) {
                query += `activityIds=${activityIds.join(',')}&`;
            }
      
            var request = AnalysisApi.exportActivityPlanShp(this.$route.params.id, this.$route.params.modelingResultId, query);
            window.open(request.url);
        },
        async updateFeatures() {
            if (this.vectorLayer) {
                this.vectorLayer.getSource().clear();
            }

            if (!this.data.limit) {
                this.formLoading = false;
                return;
            }
            // vyfiltruj sekce
            const secs = this.filteredSections;

            var tableColumns = this.tableColumns;
            var tableData = [];
            var result = [];
            secs.forEach((section) => {
                var record = this.recById["ds"+section.datasetId][section.objectid];

                //FIX: pokud nejsou vstupni data
                if(!record) return;

                result.push({ ...record, ...section });
                const baseObject = {
                    objectid: section.objectid,
                    assetType: section.assetType,
                    assetValue: section.assetValue?.toFixed(2),
                    lifeCycle: section.lifeCycle,
                    ageValue: section.ageValue,
                    weightValue: section.weightValue?.toFixed(2),
                    coreBenefit: section.coreBenefit?.toFixed(2),
                    costFactor: section.costFactor?.toFixed(2),
                    annuity: section.annuity?.toFixed(2),
                    limit: section.limit,
                    group: section.group,
                };

                section.infosets.forEach((prop) => {
                    baseObject[prop.name] = prop.value;

                    if (!tableColumns.some(col => col.data == prop.name)) {
                        tableColumns.push({
                            title: prop.alias,
                            data: prop.name,
                            width: 100
                        });
                    }
                });

                if (section.activities.length > 1) {
                    section.activities.forEach((act) => {
                        tableData.push({
                            ...baseObject,
                            name: act.name,
                            year: act.year,
                            age: act.age,
                            cost: act.cost,
                            package: act.package,
                            color: act.color,
                            assetPerformanceBefore: act.assetPerformanceBefore,
                            assetPerformanceAfter: act.assetPerformanceAfter
                        });
                    });
                } else {
                    const a = section.activities[0] || {};
                    tableData.push({
                        ...baseObject,
                        name: a.name || "",
                        year: a.year || "",
                        age: a.age || "",
                        cost: a.cost || "",
                        package: a.package || "",
                        color: a.color || "",
                        assetPerformanceBefore: a.assetPerformanceBefore,
                        assetPerformanceAfter: a.assetPerformanceAfter
                    });
                }
            });

            this.tableData = tableData;
            this.tableColumns = tableColumns;

            // vyčisti staré
            if(this.vectorLayer != null) {
                const src = this.vectorLayer.getSource();
                src.clear();

                // vytvoř nové
                const feats = getFeaturesFromSectionGeoJSON(result, this.projection);
                src.addFeatures(feats);
            }
        },
        setPopupTableData() {
            var self = this;
            new DataTablesLib(self.$refs["popUpDataTable"]).destroy();
            var currentSelectedFeature = self.selectedFeatures[self.selectedFeatureIndex - 1];
            var currentSelectedFeatureProperties = currentSelectedFeature.get("properties");

            var tmpDataTable = [];
            for(var property in currentSelectedFeatureProperties) {
                tmpDataTable.push({ name: property, value: currentSelectedFeatureProperties[property] });
            }

            self.popUpDataTable = new DataTablesLib(self.$refs["popUpDataTable"], {
                scrollY: '300px',
                scrollCollapse: true,
                paging: false,
                searching: false,
                info: false,
                columns: [
                    {
                        "data": "name",
                        "title": this.$t('attribute')
                    },
                    {
                        "data": "value",
                        "title": this.$t('value')
                    }
                ],
            }).clear().rows.add(tmpDataTable).draw();
        },
        adjustDt() {
            const api = this.$refs.dt?.dt;
            console.log(api, this)
            if (!api) return;
            api.columns.adjust();
        },
    },
    data() {
        return {
            renderMap: true,
            selectedFeatures: [],
            selectedFeatureIndex: 0,
            sections: [],
            select: null,
            map: null,
            data: {
                limit: null,
                evaluationMethod: null,
                selectionStartYear: null,
                selectionEndYear: null
            },
            tableColumns: [
                { title: this.$t('group'), data: 'group', width: 100 },
                { title: this.$t('limit'), data: 'limit', width: 100 },
                { title: this.$t('at'), data: 'assetType', width: 100 },
                { title: this.$t('recordId'), data: 'objectid', width: 100 },
                { title: this.$t('assetType'), data: 'assetType', width: 100 },
                { title: this.$t('lc'), data: 'lifeCycle', width: 100 },
                { title: this.$t('assetPerformance'), data: 'assetValue', width: 100 },
                { title: this.$t('ageValue'), data: 'ageValue', width: 100 },
                { title: this.$t('assetWeight'), data: 'weightValue', width: 100 },
                { title: this.$t('coreBenefit'), data: 'coreBenefit', width: 100 },
                { title: this.$t('costFactor'), data: 'costFactor', width: 100 },
                { title: this.$t('annuity'), data: 'annuity', width: 100 },
                { title: this.$t('year'), data: 'year', width: 100 },
                { title: this.$t('cost'), data: 'cost', width: 100 },
                { title: this.$t('age'), data: 'age', width: 100 },
                { title: this.$t('activity'), data: 'name', width: 100 },
                { title: this.$t('package'), data: 'package', width: 100 },
                { title: this.$t('performanceBefore'), data: 'assetPerformanceBefore', width: 100 },
                { title: this.$t('performanceAfter'), data: 'assetPerformanceAfter', width: 100 }
            ],
            tableData: [],
            recById: {},
            tableOptions: {
                fixedHeader: {
                    header: false,
                    footer: true
                },
                scrollX: true,
                scrollY: 350,
                order: [0, 'desc']
            },
            chartConfigs: [],
            limitList: [],
            assetTypeList: [],
            activityList: [],
            activityPlanProperties: [],
            mapCenter: "",
            pending: true,
            vectorLayer: null,
            projection: 'GEOGCS["WGS 84", DATUM["World Geodetic System 1984", SPHEROID["WGS 84", 6378137.0, 298.257223563, AUTHORITY["EPSG","7030"]], AUTHORITY["EPSG","6326"]], PRIMEM["Greenwich", 0.0, AUTHORITY["EPSG","8901"]], UNIT["degree", 0.017453292519943295], AXIS["Geodetic longitude", EAST], AXIS["Geodetic latitude", NORTH], AUTHORITY["EPSG","4326"]]',
            isUserAdmin: false,
            modelingName: "",
            legendControl: null,
            legendItems: []
        }
    }
};
</script>
<style>
     .activity-legend {
        position: absolute;
        right: 16px;
        top: 16px;
        z-index: 1000;
    }
    .activity-legend .legend-card {
        font-size: small;
        background: #fff;
        border-radius: 8px;
        padding: 10px 12px;
    }
    .activity-legend .legend-title {
        text-align: center;
        font-weight: 600;
        margin-bottom: 8px;
    }
    .activity-legend .legend-list {
        list-style: none;
        padding: 0;
        margin: 0;
    }
    .activity-legend .legend-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        white-space: nowrap;
    }
    .activity-legend .legend-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        display: inline-block;
        outline: 1px solid rgba(255,255,255,.45); 
    }
    .activity-legend .legend-label {
        overflow: hidden;
        text-overflow: ellipsis;
    }
</style>